"use client";

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ResponsiveModal } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { createEnhancedGenericForm } from "@/components/shared/EnhancedGenericCreateForm";
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { CompanyEntity } from "@/lib/types/company";

const ENHANCED_COMPANIES_CONFIG = getEntityTableConfig<CompanyEntity>("companies");
import { CompanyInfoDisplay } from "./CompanyInfoDisplay";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Alert } from "@/components/ui/alert";
import { Company, CompanyInsert, CompanyUpdate } from "@/lib/hooks/api/companies";
import { CompanyEditModal } from "./CompanyDetails/CompanyEditModal";
import { CompactDraftIndicator } from "@/components/ui/forms/draft-indicator";
import { ExternalLink, FileText, Building2 } from "lucide-react";
import Link from "next/link";
import { ComposeModalContainer } from "@/components/crm/messages/compose/ComposeModalContainer";
import { useCompanyTickets } from "@/lib/hooks/api/companies";
import { PopoverItemsSummary } from "@/components/ui/popover-items-summary";
import {
  fetchCompanyById,
  createCompany,
  updateCompany,
} from "./company-popover-api";

interface CompanyPopoverProps {
  name: string;
  companyId: string;
  children: React.ReactNode;
}

export const CompanyPopover = ({ name, companyId, children }: CompanyPopoverProps) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);

  const {
    data: company,
    isLoading,
    error,
  } = useQuery<Company | null, Error>({
    queryKey: ["company", companyId],
    queryFn: () => fetchCompanyById(companyId),
  });

  const { data: companyTickets = [] } = useCompanyTickets(company?.id, {
    limit: 10,
  });

  const createMutation = useMutation({
    mutationFn: createCompany,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["company", companyId] });
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
      setCreateModalOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CompanyUpdate) => {
      if (!company) throw new Error("Company not found");
      return updateCompany(data, company.id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["company", companyId] });
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
      setEditModalOpen(false);
    },
  });

  const handleCreateCompany = (formData: CompanyInsert) => {
    createMutation.mutate(formData);
  };

  const handleUpdateCompany = async (formData: CompanyUpdate) => {
    await updateMutation.mutateAsync(formData);
  };

  const popoverContent = (
    <div className="grid gap-4">
      <div className="space-y-2">
        <h4 className="font-medium leading-none">{name}</h4>
      </div>
      <div className="grid gap-2">
        {isLoading && <LoadingSpinner />}
        {error && <Alert>Error: {error.message}</Alert>}
        {company ? (
          <div className="space-y-3">
            <CompanyInfoDisplay company={company} sections={["company"]} />

            <PopoverItemsSummary
              items={companyTickets}
              label="Tickets"
              viewAllHref={`/companies/${company.id}#tickets`}
            />

            <div className="grid grid-cols-2 gap-2">
              <Link href={`/companies/${company.id}`}>
                <Button variant="outline" size="sm" className="gap-2">
                  <ExternalLink className="h-3 w-3" />
                  View Details
                </Button>
              </Link>

              <Link href={`/tickets?company=${company.id}`}>
                <Button variant="outline" size="sm" className="gap-2">
                  <FileText className="h-3 w-3" />
                  Tickets
                </Button>
              </Link>
            </div>

            <Button
              onClick={() => setEditModalOpen(true)}
              variant="ghost"
              size="sm"
              className="w-full gap-2"
            >
              <Building2 className="h-3 w-3" />
              Edit Company
            </Button>
          </div>
        ) : (
          <Button onClick={() => setCreateModalOpen(true)}>Create Company</Button>
        )}
      </div>
      {isCreateModalOpen && (
        <div className="mt-4 pt-4 border-t">
          {React.createElement(
            createEnhancedGenericForm("companies", ENHANCED_COMPANIES_CONFIG),
            {
              // The form is built from the `CompanyEntity` (codegen) config, so
              // that is what it hands back — not the PostgREST `Company` row.
              onSubmit: (data: Partial<CompanyEntity>) =>
                handleCreateCompany(data as unknown as CompanyInsert),
              initialValues: { name },
            }
          )}
          <Button variant="ghost" onClick={() => setCreateModalOpen(false)} className="mt-4 w-full">
            Cancel
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen(true);
          }
        }}
      >
        {children}
      </div>

      <ResponsiveModal forceModal
        open={isOpen}
        onOpenChange={setIsOpen}
        title="Company Details"
        maxWidth="md"
      >
        {popoverContent}
      </ResponsiveModal>

      {company && (
        <CompanyEditModal
          company={company}
          isOpen={isEditModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleUpdateCompany}
        />
      )}
    </>
  );
};
